'use client';

import { useEffect, useState } from 'react';
import { useOptionalUser } from '@/context/UserContext';

// Module-level cache keyed by user id — /api/sites returns the caller's
// visible sites and is invariant for the session, so a single fetch per
// signed-in user is enough. Promise entries dedupe concurrent callers.
type CacheEntry = boolean | Promise<boolean>;
const hasSitesCache: Map<number, CacheEntry> = new Map();

async function fetchHasSites(userId: number): Promise<boolean> {
  const cached = hasSitesCache.get(userId);
  if (typeof cached === 'boolean') return cached;
  if (cached instanceof Promise) return cached;
  const p = (async () => {
    try {
      const res = await fetch('/api/sites', { credentials: 'include' });
      if (!res.ok) return false;
      const data = await res.json();
      const sites = Array.isArray(data) ? data : (Array.isArray(data?.sites) ? data.sites : []);
      const result = sites.length > 0;
      hasSitesCache.set(userId, result);
      return result;
    } catch {
      return false;
    }
  })();
  hasSitesCache.set(userId, p);
  return p;
}

export type HasSitesState = {
  // null until the answer is known — callers should treat this as "still
  // deciding" and avoid showing either branch of UI that depends on it.
  hasSites: boolean | null;
};

export function useHasSites(): HasSitesState {
  const ctx = useOptionalUser();
  const userId = ctx?.user?.id;
  const [hasSites, setHasSites] = useState<boolean | null>(() => {
    if (!userId) return null;
    const cached = hasSitesCache.get(userId);
    return typeof cached === 'boolean' ? cached : null;
  });

  useEffect(() => {
    if (!userId) {
      setHasSites(null);
      return;
    }
    let cancelled = false;
    fetchHasSites(userId).then(result => {
      if (!cancelled) setHasSites(result);
    });
    return () => { cancelled = true; };
  }, [userId]);

  return { hasSites };
}
